import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useProjectFilter } from "@/context/ProjectFilterContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { EditableText } from "@/components/cms/EditableText";
import { KpiTile } from "@/components/KpiTile";
import { GovernedEditableField } from "@/components/GovernedEditableField";
import { AnalyticsFilterBar } from "@/components/filters/AnalyticsFilterBar";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps, seriesColor } from "@/lib/chart";
import { axisINR, formatCount, formatPercent, indianGroup } from "@/lib/format";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, Legend,
} from "recharts";
import {
  Building2, Layers, Network, CalendarDays, ChevronRight, Rocket, Tag,
  Target, CircleDollarSign, Percent, Gauge, Pencil, ShieldCheck,
} from "lucide-react";

const DASH = "\u2014";

const DIMS = [
  { key: "project", label: "By Project", icon: Building2 },
  { key: "stage", label: "By Stage", icon: Rocket },
  { key: "category", label: "By Category", icon: Tag },
  { key: "vertical", label: "By Vertical", icon: Layers },
  { key: "vendor", label: "By Platform & Vendor", icon: Network },
  { key: "month", label: "By Month", icon: CalendarDays },
];

export const STAGE_META = {
  Launched: { label: "Launched", color: "hsl(var(--success))" },
  Sustenance: { label: "Sustenance", color: "hsl(var(--info))" },
  Upcoming: { label: "Upcoming", color: "hsl(var(--warning))" },
  Other: { label: "Other", color: "hsl(var(--muted-foreground))" },
};

export function StageBadge({ stage, testid }) {
  const m = STAGE_META[stage] || STAGE_META.Other;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
      style={{ background: `${m.color}1f`, color: m.color }}
      data-testid={testid}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

export default function AnalyticsHub() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { money } = useCurrency();
  const { isFull, isManager, isCmsAdmin } = useAuth();
  const canEdit = isFull || isManager;
  const { period, selected } = useProjectFilter();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openPlat, setOpenPlat] = useState({});
  const [stageFilter, setStageFilter] = useState(null);
  const [head, setHead] = useState(null);
  const [projMeta, setProjMeta] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const dim = DIMS.some((d) => d.key === sp.get("dim")) ? sp.get("dim") : "project";
  const setDim = (v) => { if (v) setSp({ dim: v }); };

  const projKey = (selected || []).join(",");
  const load = () => {
    setLoading(true);
    const params = { period };
    if (projKey) params.projects = projKey;
    api.analytics(params).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, projKey]);

  // Performance economics (revenue + funnel/cost efficiency) — single source of truth for Analytics.
  const reloadHead = () => api.marketingHead(period, selected).then((d) => setHead(d)).catch(() => {});
  useEffect(() => {
    let on = true;
    api.marketingHead(period, selected).then((d) => on && setHead(d)).catch(() => {});
    return () => { on = false; };
    // eslint-disable-next-line
  }, [period, projKey]);
  useEffect(() => { api.lists().then((l) => setProjMeta(l.projects || [])).catch(() => {}); }, []);

  const k = head?.kpis || {};
  const inr = (v) => (v ? `\u20b9${indianGroup(Math.round(v))}` : DASH);
  const curated = head?.curated || [];
  const scopeProjects = (selected && selected.length) ? selected.filter((p) => curated.includes(p)) : curated;
  const singleProject = scopeProjects.length === 1 ? scopeProjects[0] : null;
  const trOf = (p) => { const m = projMeta.find((x) => x.name === p); return m ? (m.targeted_revenue || 0) : 0; };
  const saveTR = (project) => async (value, reason) => {
    await api.editDashboardValue({ project, month: "FY", field: "targeted_revenue", value, reason });
    const l = await api.lists(); setProjMeta(l.projects || []);
    await reloadHead();
  };
  const targetedRevenueValue = canEdit && singleProject ? (
    <span data-testid="analytics-targeted-revenue-value">
      <GovernedEditableField
        value={k.targeted_revenue || 0}
        display={(v) => (v ? <Money value={v} /> : DASH)}
        canEdit onSave={saveTR(singleProject)}
        label={`Targeted Revenue \u00b7 ${singleProject} \u00b7 FY`}
        testPrefix="analytics-targeted-revenue" align="left" showLock={false} valueClassName="tabular-nums"
      />
    </span>
  ) : (
    <span data-testid="analytics-targeted-revenue-value" className="inline-flex items-center gap-2">
      <Money value={k.targeted_revenue} />
      {canEdit && (
        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setEditOpen(true)} data-testid="analytics-targeted-revenue-edit-open">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      )}
    </span>
  );

  const t = data?.totals;
  const activeMonths = ["Apr-26", "May-26", "Jun-26"];
  const projectRows = (data?.by_project || []).filter((p) => !stageFilter || (p.stage || "Other") === stageFilter);

  return (
    <div className="space-y-5" data-testid="analytics-hub">
      <PageHeader title={<EditableText cmsKey="analytics.title" fallback="Analytics" />} description={<EditableText cmsKey="analytics.subtitle" fallback="Unified analytics — slice spend, budget, revenue & targets across projects, verticals, platforms and months." multiline />}>
        {isCmsAdmin && (
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => navigate("/governance/data-integrity")} data-testid="analytics-parity-sweep-button">
            <ShieldCheck className="h-3.5 w-3.5" /> Parity sweep
          </Button>
        )}
      </PageHeader>

      <AnalyticsFilterBar activeMonths={activeMonths} rightSlot={
        <ToggleGroup type="single" value={dim} onValueChange={setDim} className="rounded-lg border bg-card p-0.5" data-testid="analytics-dimension-switcher">
          {DIMS.map((d) => (
            <ToggleGroupItem key={d.key} value={d.key} className="h-7 gap-1.5 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid={`analytics-dim-${d.key}`}>
              <d.icon className="h-3.5 w-3.5" /><span className="hidden md:inline">{d.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      } />

      {/* Totals strip */}
      {t && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="analytics-totals-strip">
          <SummaryTile label="Total Spend" value={money(t.spend)} testId="analytics-total-spend" />
          <SummaryTile label="Budget Allocated" value={money(t.budget)} testId="analytics-total-budget" />
          <SummaryTile label="Revenue" value={money(t.revenue)} testId="analytics-total-revenue" />
          <SummaryTile label="Projects in scope" value={formatCount(t.projects)} testId="analytics-projects-in-scope" />
        </div>
      )}

      {/* Performance Economics — always visible, one compact strip (no extra clicks) */}
      {head && head.kpis && (
        <SectionCard
          title="Performance Economics"
          description="FY revenue goal and funnel / cost efficiency for the selected scope. Cost metrics = Spend ÷ actuals; conversion = downstream ÷ upstream."
          testId="analytics-performance-economics-card"
        >
          <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 xl:grid-cols-6" data-testid="analytics-funnel-economics">
            <KpiTile
              testId="analytics-targeted-revenue" title="Targeted Revenue" accent="hsl(var(--chart-cat-6))" icon={Target}
              value={targetedRevenueValue}
              sub={singleProject ? `FY goal \u00b7 ${singleProject}` : "Overall revenue goal"}
            />
            <MiniStat label="Cost / Lead" value={inr(k.cpl)} icon={CircleDollarSign} accent="hsl(var(--chart-cat-4))" hint="Total Spend ÷ Leads (actual)" testid="analytics-econ-cpl" />
            <MiniStat label="Cost / Site Visit" value={inr(k.cost_per_sv)} icon={CircleDollarSign} accent="hsl(var(--chart-cat-3))" hint="Total Spend ÷ Site Visits (actual)" testid="analytics-econ-cost-sv" />
            <MiniStat label="Cost / Booking" value={inr(k.cost_per_booking)} icon={CircleDollarSign} accent="hsl(var(--chart-cat-2))" hint="Total Spend ÷ Bookings (actual)" testid="analytics-econ-cost-booking" />
            <MiniStat label="Lead → SV" value={k.lead_to_sv ? formatPercent(k.lead_to_sv, 1) : DASH} icon={Percent} accent="hsl(var(--chart-cat-6))" hint="Site Visits ÷ Leads" testid="analytics-econ-lead-sv" />
            <MiniStat label="SV → Booking" value={k.sv_to_booking ? formatPercent(k.sv_to_booking, 1) : DASH} icon={Gauge} accent="hsl(var(--chart-cat-1))" hint="Bookings ÷ Site Visits" testid="analytics-econ-sv-booking" />
          </div>
        </SectionCard>
      )}

      {loading && !data ? <Loading rows={8} /> : !data ? <EmptyState title="No data" message="Could not load analytics." /> : (
        <>
          {dim === "project" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <SectionCard title="By Project" description="Funnel actuals & finance per project — Leads, Site Visits, Bookings, Revenue and Spends." className="xl:col-span-7" testId="analytics-by-project">
                <div className="flex flex-wrap items-center gap-1.5 border-b px-3 py-2" data-testid="analytics-stage-filter">
                  <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Stage</span>
                  <button
                    onClick={() => setStageFilter(null)}
                    className={cn("rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors duration-150",
                      !stageFilter ? "border-primary bg-primary text-primary-foreground" : "hover:bg-[hsl(var(--surface-2))]")}
                    data-testid="analytics-stage-filter-all"
                  >All</button>
                  {["Launched", "Sustenance", "Upcoming", "Other"].map((st) => (
                    <button
                      key={st}
                      onClick={() => setStageFilter((cur) => (cur === st ? null : st))}
                      className={cn("rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors duration-150",
                        stageFilter === st ? "border-primary bg-primary text-primary-foreground" : "hover:bg-[hsl(var(--surface-2))]")}
                      data-testid={`analytics-stage-filter-${st.toLowerCase()}`}
                    >{st}</button>
                  ))}
                </div>
                <div className="table-scroll" style={{ maxHeight: 520 }}>
                  <table className="w-full text-sm" data-testid="analytics-by-project-table">
                    <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="th-sticky-left px-3 py-2 text-left font-medium">Project</th>
                        <th className="px-3 py-2 text-right font-medium">Leads</th>
                        <th className="px-3 py-2 text-right font-medium">Site Visits</th>
                        <th className="px-3 py-2 text-right font-medium">Bookings</th>
                        <th className="px-3 py-2 text-right font-medium">Revenue</th>
                        <th className="px-3 py-2 text-right font-medium">Spends</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {projectRows.map((p) => (
                        <tr key={p.project} className="hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-by-project-row">
                          <td className="td-sticky-left px-3 py-2 font-medium">
                            <button className="text-left hover:text-primary hover:underline" onClick={() => navigate(`/projects/${encodeURIComponent(p.project)}`)}>{p.short || p.project}</button>
                            <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                              <StageBadge stage={p.stage} testid="analytics-by-project-stage" />
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid="analytics-by-project-leads-cell">{formatCount(p.leads_actual || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid="analytics-by-project-sv-cell">{formatCount(p.site_visits || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid="analytics-by-project-bookings-cell">{formatCount(p.bookings || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium" data-testid="analytics-by-project-revenue-cell"><Money value={p.revenue} /></td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid="analytics-by-project-spend-cell"><Money value={p.spend} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Spend by Project" className="xl:col-span-5" testId="analytics-by-project-chart">
                <div className="px-2 py-4" style={{ height: Math.max(280, projectRows.length * 24) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={projectRows.slice(0, 20)} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                      <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                      <YAxis type="category" dataKey="short" width={110} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                      <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                      <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={12} isAnimationActive={false}>
                        {projectRows.slice(0, 20).map((_, i) => <Cell key={i} fill={seriesColor(i)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>
          )}

          {dim === "stage" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <SectionCard title="By Stage" description="Spend, revenue & funnel actuals grouped by project lifecycle stage." className="xl:col-span-7" testId="analytics-by-stage">
                <div className="table-scroll">
                  <table className="w-full text-sm" data-testid="analytics-by-stage-table">
                    <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="th-sticky-left px-3 py-2 text-left font-medium">Stage</th>
                        <th className="px-3 py-2 text-right font-medium">Projects</th>
                        <th className="px-3 py-2 text-right font-medium">Leads</th>
                        <th className="px-3 py-2 text-right font-medium">Site Visits</th>
                        <th className="px-3 py-2 text-right font-medium">Bookings</th>
                        <th className="px-3 py-2 text-right font-medium">Revenue</th>
                        <th className="px-3 py-2 text-right font-medium">Spends</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(data.by_stage || []).map((s) => (
                        <tr key={s.stage} className="hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-by-stage-row">
                          <td className="td-sticky-left px-3 py-2 font-medium"><StageBadge stage={s.stage} /></td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCount(s.projects)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCount(s.leads_actual || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCount(s.site_visits || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCount(s.bookings || 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium"><Money value={s.revenue} /></td>
                          <td className="px-3 py-2 text-right tabular-nums"><Money value={s.spend} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Spend by Stage" className="xl:col-span-5" testId="analytics-by-stage-chart">
                <div className="px-2 py-4" style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_stage || []} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                      <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                      <YAxis type="category" dataKey="stage" width={92} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                      <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                      <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={24} isAnimationActive={false}>
                        {(data.by_stage || []).map((s, i) => <Cell key={i} fill={(STAGE_META[s.stage] || STAGE_META.Other).color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>
          )}

          {dim === "category" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <SectionCard title="By Category" description="All spends split by category / discipline (Brand, Agency, Creative, Digital, ATL, BTL, …)." className="xl:col-span-7" testId="analytics-by-category">
                <div className="table-scroll" style={{ maxHeight: 520 }}>
                  <table className="w-full text-sm" data-testid="analytics-by-category-table">
                    <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="th-sticky-left px-3 py-2 text-left font-medium">Category</th>
                        <th className="px-3 py-2 text-right font-medium">Projects</th>
                        <th className="px-3 py-2 text-right font-medium">Brand &amp; Supporting</th>
                        <th className="px-3 py-2 text-right font-medium">Total Spend</th>
                        <th className="px-3 py-2 text-right font-medium">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(data.by_category || []).map((c) => (
                        <tr key={c.category} className="hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-by-category-row">
                          <td className="td-sticky-left px-3 py-2 font-medium">{c.label}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={c.project_spend} /></td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={c.corporate_spend} /></td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium"><Money value={c.spend} /></td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatPercent(c.share, 1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Spend by Category" className="xl:col-span-5" testId="analytics-by-category-chart">
                <div className="px-2 py-4" style={{ height: Math.max(300, (data.by_category || []).length * 28) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_category || []} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                      <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                      <YAxis type="category" dataKey="label" width={104} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                      <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                      <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={13} isAnimationActive={false}>
                        {(data.by_category || []).map((_, i) => <Cell key={i} fill={seriesColor(i)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>
          )}

          {dim === "vertical" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <SectionCard title="By Vertical" description="Spend vs budget & utilisation per marketing vertical." className="xl:col-span-7" testId="analytics-by-vertical">
                <div className="table-scroll" style={{ maxHeight: 520 }}>
                  <table className="w-full text-sm" data-testid="analytics-by-vertical-table">
                    <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="th-sticky-left px-3 py-2 text-left font-medium">Vertical</th>
                        <th className="px-3 py-2 text-right font-medium">Spend</th>
                        <th className="px-3 py-2 text-right font-medium">Budget</th>
                        <th className="px-3 py-2 text-right font-medium">% Util</th>
                        <th className="px-3 py-2 text-right font-medium">Visibility</th>
                        <th className="px-3 py-2 text-right font-medium">Performance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.by_vertical.map((v) => (
                        <tr key={v.vertical} className="hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-by-vertical-row">
                          <td className="td-sticky-left px-3 py-2 font-medium">{v.label}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium"><Money value={v.spend} /></td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={v.budget} /></td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: v.util > 1 ? "hsl(var(--danger))" : "inherit" }}>{v.budget ? formatPercent(v.util, 0) : "\u2014"}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={v.visibility} /></td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={v.performance} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Spend by Vertical" className="xl:col-span-5" testId="analytics-by-vertical-chart">
                <div className="px-2 py-4" style={{ height: Math.max(280, data.by_vertical.length * 26) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_vertical} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                      <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                      <YAxis type="category" dataKey="label" width={104} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                      <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                      <Bar dataKey="spend" name="Spend" radius={[0, 4, 4, 0]} barSize={13} isAnimationActive={false}>
                        {data.by_vertical.map((_, i) => <Cell key={i} fill={seriesColor(i)} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>
          )}

          {dim === "vendor" && (
            <SectionCard title="By Platform & Vendor" description="Spend grouped by platform; expand to see vendors & pending invoices." testId="analytics-by-platform-vendor">
              <div className="divide-y" data-testid="analytics-by-platform-vendor-table">
                {data.by_platform_vendor.map((pl, i) => (
                  <Collapsible key={pl.platform} open={!!openPlat[pl.platform]} onOpenChange={() => setOpenPlat((o) => ({ ...o, [pl.platform]: !o[pl.platform] }))}>
                    <CollapsibleTrigger asChild>
                      <button className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-platform-group-toggle">
                        <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150", openPlat[pl.platform] && "rotate-90")} />
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: seriesColor(i) }} />
                        <span className="flex-1 truncate text-sm font-medium">{pl.platform}</span>
                        <span className="text-xs text-muted-foreground">{pl.lines} lines</span>
                        {pl.pending > 0 && <span className="rounded-full bg-[hsl(var(--warning)/0.12)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--warning))]">{pl.pending} pending</span>}
                        <span className="w-28 text-right text-sm font-semibold tabular-nums"><Money value={pl.spend} /></span>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mb-2 ml-10 mr-4 overflow-hidden rounded-md border bg-[hsl(var(--surface-2))]">
                        <table className="w-full text-xs">
                          <tbody>
                            {pl.vendors.map((vn) => (
                              <tr key={vn.vendor} className="border-b last:border-0">
                                <td className="px-3 py-1.5">{vn.vendor}</td>
                                <td className="px-3 py-1.5 text-right text-muted-foreground">{vn.lines} lines</td>
                                <td className="px-3 py-1.5 text-right text-muted-foreground">{vn.pending} pending</td>
                                <td className="px-3 py-1.5 text-right font-medium tabular-nums"><Money value={vn.spend} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </SectionCard>
          )}

          {dim === "month" && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <SectionCard title="By Month" description="Spend, budget, revenue & target pacing across the period." className="xl:col-span-6" testId="analytics-by-month">
                <div className="table-scroll">
                  <table className="w-full text-sm" data-testid="analytics-by-month-table">
                    <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Month</th>
                        <th className="px-3 py-2 text-right font-medium">Budget</th>
                        <th className="px-3 py-2 text-right font-medium">Spend</th>
                        <th className="px-3 py-2 text-right font-medium">Revenue</th>
                        <th className="px-3 py-2 text-right font-medium">Pacing</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.by_month.map((m) => (
                        <tr key={m.month} className="hover:bg-[hsl(var(--surface-2))]" data-testid="analytics-by-month-row">
                          <td className="px-3 py-2 font-medium">{m.month}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground"><Money value={m.budget} /></td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium"><Money value={m.spend} /></td>
                          <td className="px-3 py-2 text-right tabular-nums"><Money value={m.revenue} /></td>
                          <td className="px-3 py-2 text-right tabular-nums" style={{ color: m.pacing > 1 ? "hsl(var(--danger))" : "inherit" }}>{m.budget ? formatPercent(m.pacing, 0) : "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
              <SectionCard title="Spend vs Budget vs Revenue" className="xl:col-span-6" testId="analytics-by-month-chart">
                <div className="px-2 py-4" style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data.by_month} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
                      <XAxis dataKey="month" {...axisProps} />
                      <YAxis tickFormatter={axisINR} {...axisProps} width={46} />
                      <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="budget" name="Budget" fill="hsl(var(--chart-cat-7))" radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false} />
                      <Bar dataKey="spend" name="Spend" fill={C.spend} radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false} />
                      <Line type="monotone" dataKey="revenue" name="Revenue" stroke="hsl(var(--chart-cat-1))" strokeWidth={2} dot={false} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>
          )}
        </>
      )}

      {/* Project Performance — relocated here from the Executive Overview (PART 3 · 3.9). */}
      {head && (head.project_groups || []).length > 0 && (() => {
        const stageOf = Object.fromEntries((head.projects || []).map((p) => [p.project, p.stage || "Other"]));
        return (
          <SectionCard title="Project Performance" description="Curated portfolio scorecard — click a project to open its detail." testId="analytics-project-performance">
            <div className="space-y-4 p-4">
              {(head.project_groups || []).map((g) => (
                <div key={g.portfolio} className="rounded-xl border" data-testid={`analytics-portfolio-${g.portfolio.replace(/[^a-zA-Z]+/g, "-")}`}>
                  <div className="flex items-center justify-between border-b bg-[hsl(var(--surface-2))] px-3 py-2">
                    <p className="text-sm font-semibold">{g.portfolio}</p>
                    <p className="text-[11px] text-muted-foreground">Spent <span className="tabular-nums text-foreground">{money(g.totals.spent)}</span> of <span className="tabular-nums">{money(g.totals.allocated)}</span></p>
                  </div>
                  <div className="table-scroll">
                    <table className="w-full border-collapse">
                      <thead><tr>
                        {["Project", "Allocated", "Planned", "Spent", "Spare", "Revenue", "Status", ""].map((h, i) => (
                          <th key={h + i} className={`th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${i >= 1 && i <= 5 ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {g.rows.map((p, i) => (
                          <tr key={p.project} data-testid={`analytics-project-row-${p.project}`} onClick={() => navigate(`/projects/${encodeURIComponent(p.project)}`)}
                            className={`cursor-pointer border-b last:border-0 hover:bg-[hsl(var(--primary)/0.06)] ${i % 2 ? "row-zebra" : ""}`}>
                            <td className="td-sticky-left px-3 py-2.5 text-sm font-medium">
                              <span className="inline-flex items-center gap-1.5">
                                {p.project}
                                <StageBadge stage={stageOf[p.project] || "Other"} testid={`analytics-project-stage-${p.project}`} />
                              </span>
                            </td>
                            <td className="px-3 py-2.5 num text-sm text-muted-foreground"><Money value={p.allocated} /></td>
                            <td className="px-3 py-2.5 num text-sm"><Money value={p.planned} /></td>
                            <td className="px-3 py-2.5 num text-sm font-medium"><Money value={p.spent} /></td>
                            <td className="px-3 py-2.5 num text-sm"><Money value={p.spare} colorNegative /></td>
                            <td className="px-3 py-2.5 num text-sm"><Money value={p.revenue} /></td>
                            <td className="px-3 py-2.5">
                              {p.status === "over_plan" ? <Badge variant="outline" className="border-[hsl(var(--warning)/0.4)] text-[9px] text-[hsl(var(--warning))]">Above plan</Badge>
                                : p.status === "over_spent" ? <Badge variant="outline" className="border-[hsl(var(--danger)/0.4)] text-[9px] text-[hsl(var(--danger))]">Overspent</Badge>
                                  : <Badge variant="outline" className="border-[hsl(var(--success)/0.4)] text-[9px] text-[hsl(var(--success))]">On track</Badge>}
                            </td>
                            <td className="px-2 py-2.5 text-right"><ChevronRight className="inline h-4 w-4 text-muted-foreground" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        );
      })()}

      {/* Per-project Targeted Revenue editor (multi/all scope) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="analytics-targeted-revenue-dialog">
          <DialogHeader>
            <DialogTitle>Edit Targeted Revenue</DialogTitle>
            <DialogDescription>Set the FY targeted revenue for each project in scope. Every change is recorded in the audit log.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1" data-testid="analytics-targeted-revenue-list">
            {scopeProjects.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No curated projects in scope.</p>
            )}
            {scopeProjects.map((p) => (
              <div key={p} className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2" data-testid={`analytics-tr-row-${p.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                <span className="text-sm font-medium">{p}</span>
                <GovernedEditableField
                  value={trOf(p)}
                  display={(v) => (v ? <Money value={v} /> : DASH)}
                  canEdit onSave={saveTR(p)}
                  label={`Targeted Revenue \u00b7 ${p} \u00b7 FY`}
                  testPrefix={`analytics-tr-${p.replace(/[^a-zA-Z0-9]+/g, "-")}`} align="right"
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="analytics-targeted-revenue-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, testId }) {
  return (
    <div className="rounded-xl border bg-card p-3.5 card-shadow" data-testid={testId}>
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent, hint, testid }) {
  return (
    <div className="rounded-xl border bg-card p-3.5" data-testid={testid} title={hint || undefined}>
      <div className="flex items-center justify-between">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}><Icon className="h-4 w-4" /></span>
      </div>
      <p className="mt-2.5 text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-heading text-lg font-semibold tabular-nums" style={{ color: accent }}>{value}</p>
    </div>
  );
}
